import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Stack, Typography } from '@mui/material';
import { usePageChromeSlot } from 'contexts/PageChromeContext';

/**
 * Page header row: title + optional subtitle, and an `actions` slot.
 *
 * The actions are not drawn here. They are portalled into the fixed top bar,
 * because inside the page they scrolled away as soon as anyone started
 * working — and what lives in this slot is Publish, Save draft and Add,
 * exactly what you reach for after scrolling.
 *
 * Doing it here rather than page by page means every screen gets it and none
 * can drift: a page writes `actions` as it always did and they end up
 * somewhere that stays put.
 *
 *   <PageHeader
 *     title="Promotions"
 *     subtitle="Coupon codes customers type in at checkout."
 *     actions={<Button>New discount</Button>}
 *   />
 *
 * `leading` is the same trick at the other end of the bar, for a screen that
 * needs its own name and a way back rather than a fixed title — the page
 * editor, where the name is an editable field.
 *
 * A portal rather than handing the element up through state: `actions` is a
 * new element on every render, so state would mean setting state on every
 * render — a loop. Portalled, the buttons also stay live, which is how
 * "Publishing…" appears while it publishes.
 *
 * Outside the dashboard layout there is no bar, so they render in place.
 */
export default function PageHeader({ title, subtitle, actions, leading, mb = '20px' }) {
  const {
    setTitle, setSubtitle, actionSlot, leadingSlot, setHasActions,
  } = usePageChromeSlot();
  const hosted = typeof setTitle === 'function';

  useEffect(() => {
    if (!hosted) return undefined;
    setTitle(title || null);
    setSubtitle(subtitle || null);
    setHasActions(Boolean(actions));
    // Cleared on unmount, so the next page does not wear the last one's name
    // or keep a strip open for buttons that have gone.
    return () => { setTitle(null); setSubtitle(null); setHasActions(false); };
    // `actions` is a new element every render; only whether it exists matters
    // here, and that is what is compared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hosted, setTitle, setSubtitle, setHasActions, title, subtitle, Boolean(actions)]);

  // With a bar above, the page draws none of this: the title and its line of
  // explanation were appearing twice, once in the bar and again at the top of
  // the panel directly beneath it.
  if (hosted) {
    return (
      <>
        {leadingSlot && leading ? createPortal(leading, leadingSlot) : null}
        {actionSlot && actions ? createPortal(actions, actionSlot) : null}
      </>
    );
  }

  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      alignItems='center'
      flexWrap='wrap'
      gap='12px'
      mb={mb}
    >
      <Box>
        {leading}
        {title && <Typography variant='h6' fontWeight='700'>{title}</Typography>}
        {subtitle && (
          <Typography variant='body2' color='text.secondary'>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Box>{actions}</Box>}
    </Stack>
  );
}
